import { useContext, useEffect, useMemo, useRef, useState } from "react";
import StudentInfoDao from "../dao/StudentInfoDao";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

const useStudentImages = (
    studentList,
    schoolYearKey,
    token,
    { mimeType = "image/jpeg", fallbackImageUrl = null } = {}
) => {
    const { dispatch, state: globalState } = useContext(GlobalContext);
    const { studentImageList = {} } = globalState || {};
    const [loading, setLoading] = useState(false);

    const activeFetchRef = useRef(null);

    const targetIds = useMemo(
        () =>
            (studentList || [])
                .map((s) => s?.studentNumber)
                .filter(Boolean)
                .sort(),
        [studentList]
    );

    const missingIds = useMemo(() => targetIds.filter((id) => !studentImageList[id]), [targetIds, studentImageList]);

    const batchKey = useMemo(() => `${schoolYearKey}::${missingIds.join(",")}`, [schoolYearKey, missingIds]);

    useEffect(() => {
        const instanceId = Math.random().toString(36).substring(2, 5);

        if (!targetIds.length || !schoolYearKey || !token) {
            setLoading(false);
            return;
        }

        if (missingIds.length === 0) {
            setLoading(false);
            return;
        }

        if (activeFetchRef.current === batchKey) {
            setLoading(true);
            return;
        }

        activeFetchRef.current = batchKey;
        let isActive = true;
        setLoading(true);

        (async () => {
            const newImageMap = { ...studentImageList };

            try {
                await Promise.all(
                    missingIds.map(async (studentNumber) => {
                        try {
                            const response = await StudentInfoDao({
                                action: "studentImageRead",
                                ignoreError: true,
                                schoolYearKey,
                                studentNumber,
                                token
                            });

                            if (response?.data) {
                                const urlCreator = globalThis.URL || globalThis.webkitURL;
                                newImageMap[studentNumber] = urlCreator.createObjectURL(
                                    new Blob([response.data], { type: mimeType })
                                );
                            } else if (fallbackImageUrl) {
                                newImageMap[studentNumber] = fallbackImageUrl;
                            }
                        } catch (err) {
                            console.error(`[${instanceId}] Item Failed`, studentNumber);
                            if (fallbackImageUrl) newImageMap[studentNumber] = fallbackImageUrl;
                        }
                    })
                );

                if (isActive) {
                    dispatch({ type: "StudentImageList", studentImageList: newImageMap });
                }
            } catch (error_) {
                console.error(`[${instanceId}] FAILURE`, error_);
            } finally {
                setLoading(false);
                if (isActive) activeFetchRef.current = null;
            }
        })();

        return () => {
            isActive = false;
        };
        // The Dependency Array
        // We EXCLUDE studentImageList and missingIds here to prevent the loop.
        // We INCLUDE fallbackImageUrl and mimeType because they are stable options.
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [batchKey, targetIds.length, schoolYearKey, token, dispatch, fallbackImageUrl, mimeType]);

    return { images: studentImageList, loading };
};

export default useStudentImages;
